import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Layers, Package, Sparkles, ArrowLeft } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumb from '../components/navigation/Breadcrumb';
import productsData from '../data/completeProductsData';
import { getMedicalField } from '../data/medicalFields';
import { useMotion } from '../context/MotionContext';
import { trackPageView } from '../utils/analytics';

// Resolve a system slug -> full system object (name, tagline, images) from the catalog
const SYSTEM_BY_SLUG = (() => {
  const systems = [
    productsData.endoscopyTowerSystem,
    productsData.endoSTROBESystem,
    productsData.endoFLEXSpectarSystem,
    productsData.endoCOMPACTVeezarSystem,
    productsData.endoOFFICESpectarSystem,
    productsData.endoOFFICEVeezarSystem,
    productsData.endoSURGERYSpectarSystem,
    productsData.endoSURGERY3DSpectarSystem,
    productsData.endoPORTABLEVeezarSystem,
  ];
  const map = {};
  systems.forEach((s) => { if (s) map[s.slug] = s; });
  return map;
})();

const MedicalFieldPage = () => {
  const { fieldSlug } = useParams();
  const { prefersReducedMotion } = useMotion();
  const field = getMedicalField(fieldSlug);

  useEffect(() => {
    trackPageView(`MedicalField: ${fieldSlug}`);
  }, [fieldSlug]);

  if (!field) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-black pt-32 px-6">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-white mb-4">Medical Field Not Found</h1>
            <Link to="/system-solutions" className="btn-primary">Back to System Solutions</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${field.name} Solutions - XION Medical Philippines`}
        description={field.intro.join(' ')}
        path={`/medical-fields/${fieldSlug}`}
        image={field.heroImage}
      />
      <Header />
      <main className="min-h-screen bg-hud pt-32 px-6 pb-16" data-testid={`medical-field-page-${field.slug}`}>
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: 'System Solutions', link: '/system-solutions' },
              { label: `${field.fullName}` },
            ]}
          />

          {/* Hero */}
          <section className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <p className="terminal-label text-[#00FFD1] mb-4">{field.kicker}</p>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
                {field.name}
              </h1>
              <p className="text-xl md:text-2xl text-[#00FFD1] mb-6">{field.tagline}</p>
              {field.intro.map((para, i) => (
                <p key={i} className="text-white/70 leading-relaxed mb-4">{para}</p>
              ))}
              <Link
                to="/contact-ph"
                state={{ productName: `XION ${field.fullName} solutions` }}
                data-testid={`medical-field-cta-${field.slug}`}
                className="btn-primary mt-2"
              >
                Request a Consultation
                <ArrowRight size={16} />
              </Link>
            </motion.div>

            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="hud-card corner-brackets rounded-lg p-8 flex items-center justify-center aspect-square bg-gradient-to-br from-[#00FFD1]/10 to-transparent"
            >
              <img
                src={field.heroImage}
                alt={`XION ${field.fullName} system`}
                className="w-full h-full object-contain drop-shadow-[0_20px_45px_rgba(0,0,0,0.5)]"
                data-testid={`medical-field-hero-image-${field.slug}`}
              />
            </motion.div>
          </section>

          {/* Applications */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-white mb-6">Clinical Applications</h2>
            <div className="flex flex-wrap gap-3" data-testid={`medical-field-applications-${field.slug}`}>
              {field.applications.map((app, i) => (
                <div key={i} className="px-4 py-2 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full">
                  <span className="text-[#00FFD1] text-sm">{app}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Recommended Systems */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-white mb-2">Recommended XION Systems</h2>
            <p className="text-white/60 mb-8">Integrated system solutions configured for {field.fullName.toLowerCase()}.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {field.systems.map((entry) => {
                const sys = SYSTEM_BY_SLUG[entry.slug];
                if (!sys) return null;
                return (
                  <Link
                    key={entry.slug}
                    to={`/system-solutions/${sys.slug}`}
                    data-testid={`medical-field-system-${field.slug}-${sys.slug}`}
                    className="hud-card corner-brackets rounded-lg overflow-hidden group hover:border-[#00FFD1]/50 transition-colors flex"
                  >
                    <div className="w-32 sm:w-40 shrink-0 bg-gradient-to-br from-[#00FFD1]/10 to-transparent border-r border-white/10 flex items-center justify-center p-4">
                      {sys.images && sys.images[0] ? (
                        <img src={sys.images[0]} alt={sys.name} className="w-full h-full object-contain" loading="lazy" />
                      ) : (
                        <Layers size={48} className="text-[#00FFD1]/40" />
                      )}
                    </div>
                    <div className="p-5 flex-1">
                      <h3 className="text-xl font-bold text-white mb-1 group-hover:text-[#00FFD1] transition-colors">{sys.name}</h3>
                      <p className="text-[#00FFD1] text-xs font-mono mb-2">{sys.tagline}</p>
                      <p className="text-white/60 text-sm leading-snug">{entry.blurb}</p>
                      <span className="inline-flex items-center gap-1 mt-3 text-[#00FFD1] text-xs font-mono opacity-70 group-hover:opacity-100 transition-opacity">
                        View system <ArrowRight size={12} />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>

          {/* Recommended Products */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-white mb-2">Key Components</h2>
            <p className="text-white/60 mb-8">Endoscopes, processors and accessories for {field.fullName.toLowerCase()}.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`medical-field-products-${field.slug}`}>
              {field.products.map((p, i) => (
                <Link
                  key={i}
                  to={p.link}
                  data-testid={`medical-field-product-${field.slug}-${i}`}
                  className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5 hover:border-[#00FFD1]/50 hover:bg-[#00FFD1]/5 transition-all group"
                >
                  <div className="flex items-start gap-3 mb-2">
                    <Package size={18} className="text-[#00FFD1] mt-0.5 shrink-0" />
                    <h3 className="text-white font-semibold text-sm leading-snug group-hover:text-[#00FFD1] transition-colors">{p.name}</h3>
                  </div>
                  <p className="text-white/55 text-xs leading-relaxed pl-7">{p.blurb}</p>
                </Link>
              ))}
            </div>
          </section>

          {/* Capability Highlights */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-white mb-8">Why XION for {field.name}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {field.highlights.map((h, i) => (
                <div key={i} className="bg-[#0a0a0a] border border-white/10 rounded-lg p-6 hover:border-[#00FFD1]/40 transition-colors">
                  <Sparkles size={20} className="text-[#00FFD1] mb-3" />
                  <h3 className="text-white font-semibold mb-2">{h.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{h.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Distributor CTA */}
          <section className="bg-gradient-to-br from-[#00FFD1]/10 to-transparent border-2 border-[#00FFD1]/50 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Available in the Philippines</h2>
            <p className="text-white/70 mb-6 max-w-3xl">{productsData.distributor.statement}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact-ph"
                state={{ productName: `XION ${field.fullName} solutions` }}
                className="btn-primary flex-1 justify-center"
                data-testid={`medical-field-quote-${field.slug}`}
              >
                Request a Quotation
                <ArrowRight size={16} />
              </Link>
              <Link to="/system-solutions" className="btn-secondary flex-1 justify-center">
                <ArrowLeft size={16} />
                Explore All Systems
              </Link>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default MedicalFieldPage;
